import React, { useEffect, useRef, useMemo } from "react";
import { Animated, ViewProps } from "react-native";

import LoadingDotsView from "./LoadingDotsView";

/**
 * Container component for animated loading dots with pulsing opacity and scale effects.
 * Uses React Native's Animated API to create smooth looping animations for three dots.
 * @param props - Standard ViewProps passed through to the underlying View component
 * @returns The LoadingDotsView component with animated styles applied
 */
const LoadingDotsContainer: React.FC<ViewProps> = props => {
  // Animated.Value objects are created once via useRef().current and remain stable.
  // This is a standard React Native animation pattern documented in official guides.
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  /* eslint-disable react-hooks/refs -- Standard React Native animation pattern: Animated.Value objects are created once via useRef().current and remain stable. The interpolate() calls create derived animated values that don't change the ref. */
  const animatedStyles = useMemo(
    () => ({
      dot1: {
        opacity: dot1.interpolate({
          inputRange: [0, 1],
          outputRange: [0.3, 1],
        }),
        transform: [
          {
            scale: dot1.interpolate({
              inputRange: [0, 1],
              outputRange: [1, 1.3],
            }),
          },
        ],
      },
      dot2: {
        opacity: dot2.interpolate({
          inputRange: [0, 1],
          outputRange: [0.3, 1],
        }),
        transform: [
          {
            scale: dot2.interpolate({
              inputRange: [0, 1],
              outputRange: [1, 1.3],
            }),
          },
        ],
      },
      dot3: {
        opacity: dot3.interpolate({
          inputRange: [0, 1],
          outputRange: [0.3, 1],
        }),
        transform: [
          {
            scale: dot3.interpolate({
              inputRange: [0, 1],
              outputRange: [1, 1.3],
            }),
          },
        ],
      },
    }),
    [dot1, dot2, dot3]
  );
  useEffect(() => {
    const animate = (dot: Animated.Value, delay: number) => {
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ])
      ).start();
    };

    animate(dot1, 0);
    animate(dot2, 150);
    animate(dot3, 300);
  }, [dot1, dot2, dot3]);
  /* eslint-enable react-hooks/refs -- End of Animated.Value block */

  return <LoadingDotsView animatedStyles={animatedStyles} {...props} />;
};

export default LoadingDotsContainer;
